/* GSI notification components — countdown timer + back-in-stock capture. */

/* ── Countdown timer ──────────────────────────────────────────────── */
class GsiCountdown extends HTMLElement {
  connectedCallback() {
    this._end     = parseInt(this.dataset.end, 10);
    this._days    = this.querySelector('[data-countdown-days]');
    this._hours   = this.querySelector('[data-countdown-hours]');
    this._mins    = this.querySelector('[data-countdown-mins]');
    this._secs    = this.querySelector('[data-countdown-secs]');
    this._display = this.querySelector('.gsi-countdown__display');
    this._expired = this.querySelector('.gsi-countdown__expired');

    if (isNaN(this._end)) return;
    this._tick();
    this._timer = setInterval(() => this._tick(), 1000);
  }

  _tick() {
    const remaining = this._end - Date.now();
    if (remaining <= 0) {
      clearInterval(this._timer);
      if (this._display) this._display.hidden = true;
      if (this._expired) { this._expired.hidden = false; this._expired.removeAttribute('aria-hidden'); }
      return;
    }
    const d = Math.floor(remaining / 86400000);
    const h = Math.floor((remaining % 86400000) / 3600000);
    const m = Math.floor((remaining % 3600000) / 60000);
    const s = Math.floor((remaining % 60000) / 1000);
    if (this._days)  this._days.textContent  = d;
    if (this._hours) this._hours.textContent = String(h).padStart(2, '0');
    if (this._mins)  this._mins.textContent  = String(m).padStart(2, '0');
    if (this._secs)  this._secs.textContent  = String(s).padStart(2, '0');
  }

  disconnectedCallback() { clearInterval(this._timer); }
}
customElements.define('gsi-countdown', GsiCountdown);

/* ── Back-in-stock capture ────────────────────────────────────────────
   NOTE: this is the UI only. Point the fetch at your back-in-stock backend
   (Klaviyo back-in-stock API or an app proxy). Set window.GSI_BIS_ENDPOINT
   in theme settings/JS, or edit the default below. */
document.querySelectorAll('[data-bis-form]').forEach(form => {
  form.addEventListener('submit', async e => {
    e.preventDefault();
    const emailEl   = form.querySelector('[type="email"]');
    const email     = emailEl ? emailEl.value : '';
    const parent    = form.closest('[data-back-in-stock]');
    const variantId = parent ? parent.dataset.backInStock : '';
    const endpoint  = window.GSI_BIS_ENDPOINT || '/apps/bis/subscribe';
    const errorEl   = parent?.querySelector('[data-bis-error]');
    const submitBtn = form.querySelector('[type="submit"]');

    if (errorEl) errorEl.hidden = true;
    if (submitBtn) submitBtn.disabled = true;

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, variant_id: variantId }),
      });
      // a11y 3.3.1 (B4): only confirm on a 2xx — a 4xx/5xx must NOT run the success path.
      if (!res.ok) throw new Error('HTTP ' + res.status);

      form.hidden = true;
      form.setAttribute('aria-hidden', 'true');
      const smsUpsell = parent.querySelector('[data-bis-sms-upsell]');
      if (smsUpsell) {
        smsUpsell.hidden = false;
        smsUpsell.setAttribute('aria-hidden', 'false');
        // no auto-focus into the phone field — focus goes to the confirmation instead (no surprise jump).
      }
      const success = parent.querySelector('[data-bis-success]');
      if (success) { success.hidden = false; success.focus(); } // move focus to (and announce) the confirmation

      if (typeof gtag !== 'undefined') {
        gtag('event', 'gsi_bis_email_submitted', { variant_id: variantId });
      }
    } catch (err) {
      console.error('BIS submit error:', err);
      if (errorEl) {
        errorEl.textContent = "Sorry — we couldn't sign you up just now. Please try again.";
        errorEl.hidden = false; // role="alert" announces it
      }
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  });
});

/* BIS "Text Me" SMS opt-in — a11y 3.3.1 (M16): give the button a real handler + success/error feedback. */
document.querySelectorAll('[data-bis-sms-submit]').forEach(btn => {
  btn.addEventListener('click', async () => {
    const parent    = btn.closest('[data-back-in-stock]');
    const phoneEl   = parent?.querySelector('[type="tel"]');
    const phone     = phoneEl ? phoneEl.value.trim() : '';
    const variantId = btn.getAttribute('data-bis-sms-submit');
    const endpoint  = window.GSI_BIS_SMS_ENDPOINT || window.GSI_BIS_ENDPOINT || '/apps/bis/subscribe';
    const errorEl   = parent?.querySelector('[data-bis-error]');
    const upsell    = parent?.querySelector('[data-bis-sms-upsell]');

    if (errorEl) errorEl.hidden = true;
    if (!phone) {
      if (errorEl) { errorEl.textContent = 'Please enter a phone number.'; errorEl.hidden = false; }
      phoneEl?.focus();
      return;
    }
    btn.disabled = true;
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, variant_id: variantId, channel: 'sms' }),
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      if (upsell) {
        upsell.innerHTML = '<p class="gsi-bis__sms-confirm" tabindex="-1">Great — we\'ll text you too.</p>';
        upsell.querySelector('[tabindex]')?.focus();
      }
      if (typeof gtag !== 'undefined') gtag('event', 'gsi_bis_sms_submitted', { variant_id: variantId });
    } catch (err) {
      console.error('BIS SMS error:', err);
      if (errorEl) { errorEl.textContent = "Sorry — we couldn't sign you up for texts just now. Please try again."; errorEl.hidden = false; }
    } finally {
      btn.disabled = false;
    }
  });
});

/* ── Announcement bar (rotating + pause + prev/next + expandable +Details) ── */
class GsiAnnouncementBar extends HTMLElement {
  connectedCallback() {
    this._messages   = Array.from(this.querySelectorAll('[data-message]'));
    this._pauseBtn   = this.querySelector('[data-ann-pause]');
    this._prevBtn    = this.querySelector('[data-ann-prev]');
    this._nextBtn    = this.querySelector('[data-ann-next]');
    this._expandBtns = this.querySelectorAll('[data-ann-expand]');
    this._current  = 0;
    this._paused   = false;
    this._interval = parseInt(this.dataset.interval, 10) || 5000;
    this._timer    = null;

    if (this._messages.length > 1) {
      this._start();
      this._pauseBtn?.addEventListener('click', () => this._togglePause());
      this._prevBtn?.addEventListener('click', () => this._step(-1));
      this._nextBtn?.addEventListener('click', () => this._step(1));
    }
    this._pausedBeforeModal = false;
    this._expandBtns.forEach(btn => btn.addEventListener('click', () => this._openModal(btn)));
    this.querySelectorAll('.gsi-ann-modal').forEach(dlg => {
      dlg.querySelector('[data-ann-modal-close]')?.addEventListener('click', () => dlg.close());
      dlg.addEventListener('click', e => { if (e.target === dlg) dlg.close(); }); // click outside the panel
      dlg.addEventListener('close', () => { this._paused = this._pausedBeforeModal; });
    });
    if (this.querySelector('[data-hide-ev]')) this._checkExpertVoice();
  }

  /* ExpertVoice shoppers carry an EV.shopifyDealId cart attribute. Liquid hides flagged
     messages once it is set, but the EV app can set it after the first render, so re-check
     the cart here (a second, delayed check only for visitors arriving from ExpertVoice). */
  async _checkExpertVoice() {
    const waits = /expertvoice/i.test(document.referrer) ? [0, 2500] : [0];
    for (const wait of waits) {
      await new Promise(resolve => setTimeout(resolve, wait));
      try {
        const cart = await (await fetch('/cart.js')).json();
        if (cart.attributes && cart.attributes['EV.shopifyDealId']) { this._dropFlagged(); return; }
      } catch (err) { return; }
    }
  }
  _dropFlagged() {
    this.querySelectorAll('[data-hide-ev]').forEach(el => el.remove());
    this._messages = Array.from(this.querySelectorAll('[data-message]'));
    if (!this._messages.length) { this.remove(); return; }
    this._current = 0;
    this._messages.forEach((el, i) => {
      el.classList.toggle('is-active', i === 0);
      if (i === 0) el.removeAttribute('aria-hidden'); else el.setAttribute('aria-hidden', 'true');
    });
    if (this._messages.length < 2) {
      clearInterval(this._timer);
      this._pauseBtn?.remove();
      this.querySelector('.gsi-ann-bar__nav')?.remove();
    }
  }

  _start() { this._timer = setInterval(() => { if (!this._paused) this._step(1); }, this._interval); }
  _step(dir) { this._show((this._current + dir + this._messages.length) % this._messages.length); }
  _show(idx) {
    this._messages[this._current].classList.remove('is-active');
    this._messages[this._current].setAttribute('aria-hidden', 'true');
    this._current = idx;
    this._messages[this._current].classList.add('is-active');
    this._messages[this._current].removeAttribute('aria-hidden');
  }
  _togglePause() {
    this._paused = !this._paused;
    this._pauseBtn.setAttribute('aria-pressed', String(this._paused));
    this._pauseBtn.setAttribute('aria-label', this._paused ? 'Resume announcements' : 'Pause announcements');
  }
  _openModal(btn) {
    const dlg = this.querySelector(`#${btn.getAttribute('aria-controls')}`);
    if (!dlg || typeof dlg.showModal !== 'function') return;
    this._pausedBeforeModal = this._paused; // remember manual-pause state
    this._paused = true;                    // pause rotation while the overlay is open
    dlg.showModal();
  }
  disconnectedCallback() { clearInterval(this._timer); }
}
customElements.define('gsi-announcement-bar', GsiAnnouncementBar);

/* ── PDP reactive urgency + back-in-stock (updates on Dawn variantChange) ── */
(function initPdpAlerts() {
  const mapEl = document.querySelector('[data-gsi-variant-map]');
  if (!mapEl) return;
  let vmap = {};
  try { vmap = JSON.parse(mapEl.textContent); } catch (e) { return; }
  const urgencyEl = document.querySelector('[data-gsi-urgency]');
  const bisWrap   = document.querySelector('[data-gsi-bis]');

  function currentVariantId() {
    const u = new URLSearchParams(window.location.search).get('variant');
    if (u) return u;
    const input = document.querySelector('product-info input[name="id"], form[action*="/cart/add"] input[name="id"]');
    return input ? input.value : null;
  }

  function render(vid) {
    const v = vid != null ? vmap[String(vid)] : null;
    if (urgencyEl) {
      urgencyEl.innerHTML = (v && v.m && v.q > 0 && v.q <= 5)
        ? '<p class="gsi-urgency gsi-urgency--low-stock" role="status" aria-live="polite">Only ' + v.q + ' left — order soon.</p>'
        : '';
    }
    if (bisWrap) {
      const unavailable = v ? !v.a : false;
      bisWrap.hidden = !unavailable;
      const inner = bisWrap.querySelector('[data-back-in-stock]');
      if (inner && vid != null) inner.dataset.backInStock = vid;
    }
  }

  render(currentVariantId());
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, e => {
      const variant = e && e.data && e.data.variant;
      render(variant ? variant.id : currentVariantId());
    });
  }
})();

/* ── Free-shipping progress announcer (a11y 4.1.3, M14) ───────────────────
   The visual bar re-renders with the cart fragment, so its own live region is
   destroyed before it can announce. This persistent region (in theme.liquid,
   outside the re-rendered fragment) is updated on every cartUpdate instead. */
(function initShipLive() {
  const live = document.getElementById('gsi-ship-live');
  if (!live) return;
  const threshold = parseFloat(live.dataset.threshold || '0');
  if (!(threshold > 0)) return;
  const thresholdCents = Math.round(threshold * 100);
  let last = '';

  function update() {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
    fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then(r => r.json())
      .then(cart => {
        let msg = '';
        if (cart.item_count > 0) {
          const remaining = thresholdCents - cart.total_price;
          if (remaining > 0) {
            let amt;
            try { amt = (remaining / 100).toLocaleString(undefined, { style: 'currency', currency: cart.currency }); }
            catch (e) { amt = '$' + (remaining / 100).toFixed(2); }
            msg = `You're ${amt} away from free shipping.`;
          } else {
            msg = 'You have unlocked free shipping.';
          }
        }
        if (msg !== last) { live.textContent = msg; last = msg; } // only announce on change
      })
      .catch(() => {});
  }

  // Announce on cart changes only (not initial load — the visual bar already conveys state on arrival).
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, update);
  }
})();
